import { Plus, X } from "lucide-react";
import { Input, Select, Field, Label } from "@/components/rent/core/ui/Input";
import { Button } from "@/components/rent/core/ui/Button";
import { fmtINR } from "@/components/rent/core/format";

export interface Split {
  amount_paise: number;
  payment_mode: "CASH" | "CHEQUE" | "NEFT" | "UPI" | "IMPS" | "BANK_TRANSFER";
  reference_no?: string;
  notes?: string;
}

const MODES: Split["payment_mode"][] = ["UPI", "NEFT", "CASH", "CHEQUE", "IMPS", "BANK_TRANSFER"];

export function SplitPaymentEditor({
  value, onChange, dueAmountPaise,
}: {
  value: Split[];
  onChange: (v: Split[]) => void;
  dueAmountPaise: number;
}) {
  const total = value.reduce((s, r) => s + (r.amount_paise || 0), 0);
  const diff = total - dueAmountPaise;

  function update(idx: number, patch: Partial<Split>) {
    const next = value.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(next);
  }
  function remove(idx: number) {
    onChange(value.filter((_, i) => i !== idx));
  }
  function add() {
    onChange([...value, { amount_paise: Math.max(0, dueAmountPaise - total), payment_mode: "UPI" }]);
  }

  return (
    <div className="space-y-3">
      {value.map((row, idx) => (
        <div key={idx} className="grid grid-cols-12 gap-2 items-end relative">
          <div className="col-span-5">
            <Field>
              <Label>Amount (₹)</Label>
              <Input
                type="number"
                min={1}
                value={row.amount_paise / 100 || ""}
                onChange={(e) =>
                  update(idx, {
                    amount_paise: Math.round(Number(e.target.value || 0) * 100),
                  })
                }
                aria-label={`amount-${idx}`}
              />
            </Field>
          </div>
          <div className="col-span-4">
            <Field>
              <Label>Mode</Label>
              <Select
                value={row.payment_mode}
                onChange={(e) =>
                  update(idx, { payment_mode: e.target.value as Split["payment_mode"] })
                }
                aria-label={`mode-${idx}`}
              >
                {MODES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="col-span-3">
            <Field>
              <Label>Ref</Label>
              <Input
                value={row.reference_no ?? ""}
                onChange={(e) => update(idx, { reference_no: e.target.value })}
                aria-label={`ref-${idx}`}
              />
            </Field>
          </div>
          {value.length > 1 && (
            <button
              type="button"
              onClick={() => remove(idx)}
              className="absolute -right-1 -top-1 text-[var(--cp-ink-3)] hover:text-red-500"
              aria-label={`remove-split-${idx}`}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      ))}

      <div className="flex items-center justify-between border-t border-[var(--cp-line)] pt-3">
        <Button type="button" variant="outline" size="sm" onClick={add}>
          <Plus className="h-4 w-4" /> Add split
        </Button>
        <div className="text-sm">
          Total: <span className="font-semibold">{fmtINR(total)}</span> / {fmtINR(dueAmountPaise)}
          {diff < 0 && <span className="ml-2 text-amber-600">short by {fmtINR(-diff)}</span>}
          {diff > 0 && <span className="ml-2 text-emerald-600">over by {fmtINR(diff)}</span>}
        </div>
      </div>
    </div>
  );
}
